import { useQuery } from '@tanstack/react-query'

import { api } from '@/lib/api/client'
import { humanizeCode } from '@/lib/format'

/**
 * Display names for the API's fixed codes, from GET /v1/labels — one list the
 * web and the mobile app both read, so they never describe the same thing two
 * ways. Product type is not in it (it is the lender's free text); anything the
 * map lacks goes through humanizeCode.
 */
export type LabelDomain =
  | 'openReason'
  | 'closeReason'
  | 'channel'
  | 'paymentMode'
  | 'visitReason'
  | 'visitOutcome'
  | 'visitApproval'
  | 'channelType'
  | 'captureMode'
  | 'applicableFor'

type LabelMap = Partial<Record<LabelDomain, Record<string, string>>>

export function useLabels() {
  return useQuery({
    queryKey: ['labels'],
    queryFn: async () => {
      const res = await api.get<LabelMap>('/v1/labels')
      return res.data
    },
    // Words, not data: they change only with a deploy.
    staleTime: Infinity,
  })
}

/** `label('openReason', 'DPD_THRESHOLD_CROSSED')` → "Went overdue". Never throws, never blank. */
export function useLabel() {
  const { data } = useLabels()
  return (domain: LabelDomain, code: string | null | undefined): string =>
    (code && data?.[domain]?.[code]) || humanizeCode(code)
}
